import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Table, Row, Col, Form, Alert, Badge, Modal } from 'react-bootstrap';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import databaseService from '../services/database';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { formatDate } from '../utils/dateFormatter';
import { getReviewers } from '../apis/reviewers';

// ===== AVERAGE COMPRESSIVE STRENGTH ROUNDING UTILITY =====
// Custom rounding logic for Average Compressive Strength
// Rules:
// 1. Decimal (0, 0.25] → round down to .0
// 2. Decimal (0.25, 0.75] → round to .5
// 3. Decimal (0.75, 1) → round up to next whole number .0
function roundAverageStrength(value) {
  if (!value || isNaN(parseFloat(value))) {
    return '0.0';
  }
  
  const numValue = parseFloat(value);
  const wholePart = Math.floor(numValue);
  const decimalPart = numValue - wholePart;
  
  console.log(`🔢 Rounding ${numValue}: whole=${wholePart}, decimal=${decimalPart.toFixed(3)}`);
  
  let roundedValue;
  
  if (decimalPart > 0 && decimalPart <= 0.25) {
    // Round down to .0
    roundedValue = wholePart.toFixed(1);
    console.log(`   → Decimal ≤ 0.25: Round down to ${roundedValue}`);
  } else if (decimalPart > 0.25 && decimalPart <= 0.75) {
    // Round to .5
    roundedValue = (wholePart + 0.5).toFixed(1);
    console.log(`   → Decimal (0.25, 0.75]: Round to ${roundedValue}`);
  } else if (decimalPart > 0.75) {
    // Round up to next whole number
    roundedValue = (wholePart + 1).toFixed(1);
    console.log(`   → Decimal > 0.75: Round up to ${roundedValue}`);
  } else {
    // Decimal is exactly 0 or negative (shouldn't happen, but handle it)
    roundedValue = wholePart.toFixed(1);
    console.log(`   → Decimal = 0: Keep as ${roundedValue}`);
  }
  
  return roundedValue;
}

const TestObservations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const { testRequestId: urlTestRequestId } = useParams();
  
  // Debug: Log the raw location object
  console.log('🔍 TestObservations - Raw location object:', location);

  // Get test data from location state or will fetch from API
  const { testData, testRequest, testIndex, editMode, fromGraph } = location.state || {};

  // Debug: Log destructured state variables
  console.log('🔍 TestObservations - Destructured state:');
  console.log('  editMode (destructured):', editMode);
  console.log('  testRequest (destructured):', testRequest);
  console.log('  testData (destructured):', testData);
  console.log('  fromGraph (destructured):', fromGraph);

  // Get testRequestId from URL params, location state, or testRequest object
  const testRequestId = urlTestRequestId || testRequest?.id || testData?.test_request_id;
  
  const [apiTestRequest, setApiTestRequest] = useState(null);
  const [apiConcreteTests, setApiConcreteTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  
  // Check if we should load saved data based on status
  // If status is "pending" -> don't load (fresh form)
  // If status is anything else -> load saved data
  const shouldLoadSavedData = editMode || 
    (currentStatus && currentStatus !== 'pending') ||
    (testRequest?.status && testRequest.status !== 'pending') ||
    (testData?.status && testData.status !== 'pending');
  
  // Debug: Log the status information
  console.log('🔍 TestObservations Debug:');
  console.log('  editMode:', editMode);
  console.log('  testRequest:', testRequest);
  console.log('  testRequest?.status:', testRequest?.status);
  console.log('  testData:', testData);
  console.log('  testData?.status:', testData?.status);
  console.log('  shouldLoadSavedData:', shouldLoadSavedData);
  console.log('  currentStatus (from API):', currentStatus);
  console.log('  location.state:', location.state);
  console.log('  location.state?.editMode:', location.state?.editMode);
  console.log('  location.state?.testRequest:', location.state?.testRequest);
  console.log('  location.state?.fromGraph:', location.state?.fromGraph);
  
  // Additional debug for edit mode
  if (editMode) {
    console.log('🎯 EDIT MODE DETECTED - Should load saved data immediately');
  }
  
  // Fetch current status from API to determine if we should load data
  useEffect(() => {
    const fetchCurrentStatus = async () => {
      if (testRequestId) {
        try {
          const response = await axios.get(`${API_BASE_URL}/test-requests/${testRequestId}/details`);
          const status = response.data?.status;
          setCurrentStatus(status);
          console.log('🔍 Current status from API:', status);
          
          // If status is not pending, trigger data loading
          if (status && status !== 'pending') {
            console.log('✅ Status is not pending, will load saved data');
          } else {
            console.log('ℹ️ Status is pending, will show fresh form');
          }
        } catch (error) {
          console.log('❌ Error fetching current status:', error);
        }
      }
    };
    
    fetchCurrentStatus();
  }, [testRequestId]);

  // Fetch reviewers for Tested By / Checked By dropdowns (from Reviewer Management)
  useEffect(() => {
    const fetchReviewers = async () => {
      try {
        const data = await getReviewers();
        setReviewers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching reviewers:', err);
      }
    };
    fetchReviewers();
  }, []);
  
  const [formData, setFormData] = useState({
    sampleDescription: 'Concrete Cube Specimen',
    cubeCondition: 'Acceptable',
    curingCondition: '',
    machineUsed: 'Fully Automatic Digital Compression Testing Machine',
    testMethod: 'IS 516 (Part1/Sec1):2021',
    averageStrength: '',
    testedBy: '',
    checkedBy: '',
    verifiedBy: 'Mr. P A Sanghave',
    testRemarks: '',
    rows: [],
    // Observations data for PDF display
    obsStrengthDuration: '',
    obsTestResults: '',
    obsWeight: '',
    obsFailurePattern: '',
    obsBonding: '',
    obsStrengthCriteria: ''
  });

  const [testRows, setTestRows] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [capturedImages, setCapturedImages] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesLoadProgress, setImagesLoadProgress] = useState(0);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [validationErrors, setValidationErrors] = useState({});
  const [saveProgress, setSaveProgress] = useState(0);
  const [progressInterval, setProgressInterval] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePhotoKey, setDeletePhotoKey] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lastSavedRowsData, setLastSavedRowsData] = useState([]);
  
  // Fetch test request data if URL has testRequestId
  useEffect(() => {
    const fetchTestData = async () => {
      if (testRequestId && !testData && !testRequest) {
        setLoading(true);
        try {
          const response = await axios.get(`${API_BASE_URL}/test-requests/${testRequestId}/details`);
          setApiTestRequest(response.data.test_request);
          setApiConcreteTests(response.data.concrete_tests);
          console.log('✅ Fetched test data for observations:', response.data);
          console.log('🔍 API Test Request Data:', response.data.test_request);
          console.log('🔍 API Receipt Date:', response.data.test_request?.receipt_date);
        } catch (error) {
          console.error('Error fetching test data:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchTestData();
  }, [testRequestId]);

  // Load saved data when in edit mode OR observations_completed status
  useEffect(() => {
    const loadSavedData = async () => {
      // For edit mode, load immediately. For other cases, wait for status check
      if (editMode && testRequestId) {
        console.log('📥 Loading saved data (EDIT MODE)...');
        console.log('📥 editMode:', editMode);
        console.log('📥 testRequestId:', testRequestId);
        console.log('📥 API_BASE_URL:', API_BASE_URL);
        console.log('📥 Full URL:', `${API_BASE_URL}/test-observations/${testRequestId}`);
        setLoading(true);
        try {
          console.log('🌐 Making API request to:', `${API_BASE_URL}/test-observations/${testRequestId}`);
          const response = await axios.get(
            `${API_BASE_URL}/test-observations/${testRequestId}`,
            {
              headers: {
                'Content-Type': 'application/json',
              },
              timeout: 10000
            }
          );
          console.log('✅ API response received:', response.status);
          console.log('✅ API response headers:', response.headers);
          console.log('✅ API response data type:', typeof response.data);
          console.log('✅ API response data keys:', Object.keys(response.data || {}));
          const savedData = response.data;
          
          console.log('✅ Loaded saved observations (EDIT MODE)');
          console.log('✅ Test Rows:', savedData.testRows?.length || 0, 'cubes');
          console.log('✅ Form Data:', savedData.formData);
          console.log('✅ Full API Response:', savedData);
          
          // Pre-fill form data
          if (savedData.formData) {
            setFormData(prev => {
              const newFormData = {
                ...prev,
                sampleDescription: savedData.formData.sampleDescription || 'Concrete Cube Specimen',
                cubeCondition: savedData.formData.cubeCondition || 'Acceptable',
                curingCondition: savedData.formData.curingCondition || '',
                machineUsed: savedData.formData.machineUsed || 'Fully Automatic Digital Compression Testing Machine',
                testMethod: savedData.formData.testMethod || 'IS 516 (Part1/Sec1):2021',
                averageStrength: savedData.formData.averageStrength || '',
                testedBy: savedData.formData.testedBy || '',
                checkedBy: savedData.formData.checkedBy || '',
                verifiedBy: savedData.formData.verifiedBy || 'Mr. P A Sanghave',
                testRemarks: savedData.formData.testRemarks || ''
              };
              console.log('✅ Setting form data (EDIT MODE):', newFormData);
              return newFormData;
            });
            console.log('✅ Form data pre-filled (EDIT MODE)');
          }
          
          // Pre-fill test rows
          if (savedData.testRows && savedData.testRows.length > 0) {
            console.log(`✅ Setting ${savedData.testRows.length} test rows (EDIT MODE)`);
            console.log('✅ Test rows data:', savedData.testRows);
            setTestRows(savedData.testRows);
            console.log('✅ Test rows set successfully');
          } else {
            console.log('⚠️ No test rows found in saved data');
          }
          
          // Pre-fill captured images with loading indicator
          if (savedData.capturedImages && Object.keys(savedData.capturedImages).length > 0) {
            setImagesLoading(true);
            const totalImages = Object.keys(savedData.capturedImages).length;
            let loadedCount = 0;
            
            // Fix double-prefix issue and load images with progress tracking
            const fixedImages = {};
            const imagePromises = Object.entries(savedData.capturedImages).map(([key, value]) => {
              return new Promise((resolve) => {
                // Fix double data:image prefix if it exists
                let cleanedValue = value;
                if (value && value.startsWith('data:image/')) {
                  // Check for double prefix: data:image/X;base64,data:image/Y;base64,
                  const doublePrefix = value.match(/^data:image\/[^;]+;base64,data:image\//);
                  if (doublePrefix) {
                    // Extract the actual base64 data after the second prefix
                    const secondPrefixStart = value.indexOf(',') + 1;
                    cleanedValue = value.substring(secondPrefixStart);
                    console.log(`🔧 Fixed double prefix for ${key}`);
                  }
                }
                
                fixedImages[key] = cleanedValue;
                
                const img = new Image();
                img.onload = () => {
                  loadedCount++;
                  setImagesLoadProgress(Math.round((loadedCount / totalImages) * 100));
                  resolve();
                };
                img.onerror = () => {
                  loadedCount++;
                  setImagesLoadProgress(Math.round((loadedCount / totalImages) * 100));
                  console.log(`❌ Failed to load image ${key}`);
                  resolve();
                };
                img.src = cleanedValue;
              });
            });
            
            setCapturedImages(fixedImages);
            
            // Wait for all images to load
            Promise.all(imagePromises).then(() => {
              console.log(`✅ ${totalImages} images loaded successfully! (EDIT MODE)`);
              setTimeout(() => {
                setImagesLoading(false);
                setImagesLoadProgress(0);
              }, 500);
            });
          }
          
          console.log('✅ All saved data loaded successfully! (EDIT MODE)');
          setLoading(false);
        } catch (error) {
          console.error('❌ Error loading saved data (EDIT MODE):', error);
          console.error('❌ Error type:', error.constructor.name);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error details:', error.response?.data);
          console.error('❌ Error status:', error.response?.status);
          console.error('❌ Error URL:', `${API_BASE_URL}/test-observations/${testRequestId}`);
          console.error('❌ Full error object:', error);
          
          // Check if it's a network error
          if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
            console.error('❌ Network error - Backend might not be running');
          }
          
          setLoading(false);
        }
      } else if (currentStatus !== null && shouldLoadSavedData && testRequestId) {
        console.log('📥 Loading saved observations (STATUS-BASED)...');
        console.log('📥 Test Request ID:', testRequestId);
        console.log('📥 Reason: editMode=', editMode, 'currentStatus=', currentStatus);
        console.log('📥 shouldLoadSavedData:', shouldLoadSavedData);
        console.log('🌐 API Base URL:', API_BASE_URL);
        console.log('🌐 Full URL:', `${API_BASE_URL}/test-observations/${testRequestId}`);
        setLoading(true);
        try {
          const response = await axios.get(
            `${API_BASE_URL}/test-observations/${testRequestId}`,
            {
              headers: {
                'Content-Type': 'application/json',
              },
              timeout: 10000
            }
          );
          console.log('✅ API response received (STATUS-BASED):', response.status);
          console.log('✅ API response headers (STATUS-BASED):', response.headers);
          console.log('✅ API response data type (STATUS-BASED):', typeof response.data);
          console.log('✅ API response data keys (STATUS-BASED):', Object.keys(response.data || {}));
          const savedData = response.data;
          
          console.log('✅ Loaded saved observations');
          console.log('✅ Test Rows:', savedData.testRows?.length || 0, 'cubes');
          
          // Check if data is empty (no saved observations yet)
          if (savedData.isEmpty) {
            console.log('ℹ️ No saved observations found - user can enter fresh data');
            setLoading(false);
            return;
          }
          
          // Pre-fill form data
          if (savedData.formData) {
            setFormData(prev => ({
              ...prev,
              sampleDescription: savedData.formData.sampleDescription || 'Concrete Cube Specimen',
              cubeCondition: savedData.formData.cubeCondition || 'Acceptable',
              curingCondition: savedData.formData.curingCondition || '',
              machineUsed: savedData.formData.machineUsed || 'Fully Automatic Digital Compression Testing Machine',
              testMethod: savedData.formData.testMethod || 'IS 516 (Part1/Sec1):2021',
              averageStrength: savedData.formData.averageStrength || '',
              testedBy: savedData.formData.testedBy || '',
              checkedBy: savedData.formData.checkedBy || '',
              verifiedBy: savedData.formData.verifiedBy || 'Mr. P A Sanghave',
              testRemarks: savedData.formData.testRemarks || ''
            }));
            console.log('✅ Form data pre-filled');
          }
          
          // Pre-fill test rows
          if (savedData.testRows && savedData.testRows.length > 0) {
            console.log(`✅ Setting ${savedData.testRows.length} test rows`);
            setTestRows(savedData.testRows);
          }
          
          // Pre-fill captured images with loading indicator
          if (savedData.capturedImages && Object.keys(savedData.capturedImages).length > 0) {
            setImagesLoading(true);
            const totalImages = Object.keys(savedData.capturedImages).length;
            let loadedCount = 0;
            
            // Fix double-prefix issue and load images with progress tracking
            const fixedImages = {};
            const imagePromises = Object.entries(savedData.capturedImages).map(([key, value]) => {
              return new Promise((resolve) => {
                // Fix double data:image prefix if it exists
                let cleanedValue = value;
                if (value && value.startsWith('data:image/')) {
                  // Check for double prefix: data:image/X;base64,data:image/Y;base64,
                  const doublePrefix = value.match(/^data:image\/[^;]+;base64,data:image\//);
                  if (doublePrefix) {
                    // Extract the actual base64 data after the second prefix
                    const secondPrefixStart = value.indexOf(',') + 1;
                    cleanedValue = value.substring(secondPrefixStart);
                    console.log(`🔧 Fixed double prefix for ${key}`);
                  }
                }
                
                fixedImages[key] = cleanedValue;
                
                const img = new Image();
                img.onload = () => {
                  loadedCount++;
                  setImagesLoadProgress(Math.round((loadedCount / totalImages) * 100));
                  resolve();
                };
                img.onerror = () => {
                  loadedCount++;
                  setImagesLoadProgress(Math.round((loadedCount / totalImages) * 100));
                  console.log(`❌ Failed to load image ${key}`);
                  resolve();
                };
                img.src = cleanedValue;
              });
            });
            
            setCapturedImages(fixedImages);
            
            // Wait for all images to load
            Promise.all(imagePromises).then(() => {
              console.log(`✅ ${totalImages} images loaded successfully!`);
              setTimeout(() => {
                setImagesLoading(false);
                setImagesLoadProgress(0);
              }, 500);
            });
          }
          
          
          // TEST: Try to manually set an image to see if it works
          if (Object.keys(capturedImages).length > 0) {
            const firstKey = Object.keys(capturedImages)[0];
            const firstImage = capturedImages[firstKey];
            console.log('🧪 TEST: First image key:', firstKey);
            console.log('🧪 TEST: First image data length:', firstImage ? firstImage.length : 0);
            console.log('🧪 TEST: First image starts with:', firstImage ? firstImage.substring(0, 50) : 'No data');
            
            // Force update the state
            setTimeout(() => {
              console.log('🧪 TEST: Forcing capturedImages state update...');
              setCapturedImages({...capturedImages});
            }, 1000);
          }
          
        } catch (error) {
          console.error('❌ Error loading saved observations:', error);
          console.error('❌ Error type:', error.constructor.name);
          console.error('❌ Error message:', error.message);
          console.error('❌ Error code:', error.code);
          console.error('❌ Error response:', error.response);
          console.error('❌ Full error object:', error);
          
          if (error.response) {
            console.error('❌ Server error:', error.response.data);
            console.error('❌ Server status:', error.response.status);
          } else if (error.request) {
            console.error('❌ No response from server - Backend might not be running');
          } else {
            console.error('❌ Request setup error');
          }
          
          // Check if it's a network error
          if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
            console.error('❌ Network error - Backend might not be running');
          }
          
          // Show error to user
          setErrorMessage('Could not load saved observations. You can still enter data manually.');
          setShowErrorModal(true);
          setTimeout(() => setShowErrorModal(false), 3000);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadSavedData();
  }, [editMode, currentStatus, shouldLoadSavedData, testRequestId]);

  // Initialize test rows based on number of cubes (like previous Python system)
  useEffect(() => {
    // Skip initialization if loading saved data from backend
    if (shouldLoadSavedData) {
      console.log('⏭️ Skipping test rows initialization - Loading saved data');
      console.log('⏭️ shouldLoadSavedData:', shouldLoadSavedData);
      console.log('⏭️ editMode:', editMode);
      console.log('⏭️ currentStatus:', currentStatus);
      return;
    }
    
    // Get number of cubes from test data (like Python: {{ test.num_of_cubes|int }})
    const numCubes = testData?.quantity || apiConcreteTests?.[0]?.quantity || apiConcreteTests?.length || 3;
    const actualCubes = Math.min(Math.max(numCubes, 1), 3); // Max 3 cubes like Python
    
    // Get the ID Mark from test data to auto-populate Cube ID
    const idMark = testData?.idMark || apiConcreteTests?.[0]?.id_mark || '';
    
    console.log(`🔧 Initializing ${actualCubes} cube rows (like Python generateRowsBasedOnCubes())`);
    console.log(`🔧 Auto-populating Cube ID with ID Mark: ${idMark}`);
    
    const initialRows = [];
    for (let i = 1; i <= actualCubes; i++) {
      initialRows.push({
        id: i,
        cubeId: idMark, // Auto-populate with ID Mark from test request
        length: '',
        breadth: '',
        height: '',
        area: '',
        weight: '',
        density: '',
        crushingLoad: '',
        compressiveStrength: '',
        failureType: ''
      });
    }
    setTestRows(initialRows);
  }, [testData, apiConcreteTests, shouldLoadSavedData]);

  // Debug: Monitor testRows state changes
  useEffect(() => {
    console.log('🔍 TestRows state changed:', testRows);
    console.log('🔍 TestRows length:', testRows.length);
  }, [testRows]);

  // Debug: Monitor formData state changes
  useEffect(() => {
    console.log('🔍 FormData state changed:', formData);
    console.log('🔍 Curing Condition:', formData.curingCondition);
    console.log('🔍 Tested By:', formData.testedBy);
  }, [formData]);

  // DISABLED - Don't load old localStorage data (causes dummy data issue)
  // Load saved data if available
  // useEffect(() => {
  //   const loadSavedData = async () => {
  //     try {
  //       const testRequestId = testRequest?.id || testData?.id;
  //       if (testRequestId) {
  //         // Try to load from database first
  //         const savedData = await databaseService.getTestObservations(testRequestId);
  //         if (savedData) {
  //           setFormData(prev => ({ ...prev, ...savedData.formData }));
  //           setTestRows(savedData.testRows || []);
  //           setCapturedImages(savedData.capturedImages || {});
  //           return;
  //         }
  //       }
  //       
  //       // Fallback to localStorage
  //       const storageKey = `test_observations_${testRequest?.id || testData?.id || 'temp'}`;
  //       const savedData = localStorage.getItem(storageKey);
  //       if (savedData) {
  //         const parsedData = JSON.parse(savedData);
  //         setFormData(prev => ({ ...prev, ...savedData.formData }));
  //         setTestRows(parsedData.testRows || []);
  //         setCapturedImages(parsedData.capturedImages || {});
  //       }
  //     } catch (error) {
  //       console.warn('Failed to load saved data:', error);
  //     }
  //   };

  //   loadSavedData();
  // }, [testRequest?.id, testData?.id]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [progressInterval]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateWeight = (weight) => {
    const weightNum = parseFloat(weight);
    if (isNaN(weightNum)) return 'Please enter a valid number';
    // Weight can be any value - no restrictions
    return null;
  };

  const handleRowChange = (rowIndex, field, value) => {
    setTestRows(prev => {
      const updatedRows = prev.map((row, index) => {
        if (index === rowIndex) {
          const updatedRow = { ...row, [field]: value };
          
          // Validate weight field
          if (field === 'weight') {
            const weightError = validateWeight(value);
            setValidationErrors(prev => ({
              ...prev,
              [`weight_${rowIndex}`]: weightError
            }));
          }
          
          // Calculate area if length and breadth are provided
          if (field === 'length' || field === 'breadth') {
            const length = field === 'length' ? parseFloat(value) : parseFloat(row.length);
            const breadth = field === 'breadth' ? parseFloat(value) : parseFloat(row.breadth);
            
            if (length && breadth && length > 0 && breadth > 0) {
              updatedRow.area = (length * breadth).toFixed(3);
            }
          }
          
          // Calculate density if all dimensions and weight are provided
          if (['length', 'breadth', 'height', 'weight'].includes(field)) {
            const length = field === 'length' ? parseFloat(value) : parseFloat(row.length);
            const breadth = field === 'breadth' ? parseFloat(value) : parseFloat(row.breadth);
            const height = field === 'height' ? parseFloat(value) : parseFloat(row.height);
            const weight = field === 'weight' ? parseFloat(value) : parseFloat(row.weight);
            
            if (length && breadth && height && weight && 
                length > 0 && breadth > 0 && height > 0 && weight > 0) {
              const lengthInMeters = length / 1000;
              const breadthInMeters = breadth / 1000;
              const heightInMeters = height / 1000;
              const volumeInCubicMeters = lengthInMeters * breadthInMeters * heightInMeters;
              const density = weight / volumeInCubicMeters;
              updatedRow.density = density.toFixed(3);
            }
          }
          
          return updatedRow;
        }
        return row;
      });

      // Auto-calculate average compressive strength
      const validStrengths = updatedRows
        .map(row => parseFloat(row.compressiveStrength))
        .filter(strength => !isNaN(strength) && strength > 0);
      
      if (validStrengths.length > 0) {
        // Calculate average first
        const calculatedAverage = (validStrengths.reduce((sum, strength) => sum + strength, 0) / validStrengths.length);
        
        // Apply rounding logic (same as finalTestReport.html)
        const roundedAverage = roundAverageStrength(calculatedAverage);
        
        console.log('📊 Average calculation:', {
          calculated: calculatedAverage.toFixed(4),
          rounded: roundedAverage,
          strengths: validStrengths
        });
        
        setFormData(prev => ({
          ...prev,
          averageStrength: roundedAverage
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          averageStrength: ''
        }));
      }

      return updatedRows;
    });
  };

  // Rows are automatically generated based on number of cubes from test request

  const removeRow = (rowIndex) => {
    if (testRows.length > 1) {
      setTestRows(prev => prev.filter((_, index) => index !== rowIndex));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });
    setSaveProgress(0);
    
    // Check for validation errors before submitting
    const hasValidationErrors = Object.values(validationErrors).some(error => error !== null);
    if (hasValidationErrors) {
      setErrorMessage('Please fix validation errors before submitting.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      setIsSubmitting(false);
      return;
    }
    
    // Validate all required fields are filled
    if (!formData.curingCondition || formData.curingCondition.trim() === '') {
      setErrorMessage('Please fill in all required fields (Curing Condition is required).');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      setIsSubmitting(false);
      return;
    }
    
    // Validate all test rows have required data
    const hasEmptyRows = testRows.some(row => 
      !row.cubeId || 
      !row.weight || 
      !row.length || 
      !row.breadth || 
      !row.height || 
      !row.crushingLoad || 
      !row.compressiveStrength
    );
    
    if (hasEmptyRows) {
      setErrorMessage('Please fill in all test result fields for all cubes.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      setIsSubmitting(false);
      return;
    }
    
    // Validate all 3 images are captured for each cube
    const totalCubes = testRows.length;
    for (let i = 0; i < totalCubes; i++) {
      const frontImage = capturedImages[`front_failure_${i + 1}`];
      const digitalImage = capturedImages[`digital_reading_${i + 1}`];
      const backImage = capturedImages[`back_failure_${i + 1}`];
      
      if (!frontImage || !digitalImage || !backImage) {
        setErrorMessage(`Please capture all 3 photos (Front, Digital, Back) for Cube/Core Specimen #${i + 1}.`);
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 3000);
        setIsSubmitting(false);
        return;
      }
    }
    
    // Validate average strength is calculated
    if (!formData.averageStrength || formData.averageStrength === '') {
      setErrorMessage('Please calculate the average strength before saving.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      setIsSubmitting(false);
      return;
    }
    
    // Validate Tested By and Checked By are filled
    if (!formData.testedBy || formData.testedBy.trim() === '') {
      setErrorMessage('Please enter "Tested By" name.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      setIsSubmitting(false);
      return;
    }
    
    if (!formData.checkedBy || formData.checkedBy.trim() === '') {
      setErrorMessage('Please enter "Checked By" name.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Optimized: Show minimal progress for real-time feedback
      setSaveProgress(10);
      
      // Build rowsData array like Python system
      const rowsData = [];
      
      testRows.forEach((row, index) => {
        const rowIndex = index + 1;
        
        // Check if row has essential data
        const hasCrushingLoad = row.crushingLoad && row.crushingLoad.trim() !== '';
        const hasCompressiveStrength = row.compressiveStrength && row.compressiveStrength.trim() !== '';
        
        console.log(`   - crushingLoad: "${row.crushingLoad}" (has: ${hasCrushingLoad})`);
        console.log(`   - compressiveStrength: "${row.compressiveStrength}" (has: ${hasCompressiveStrength})`);
        
        // Only include rows that have essential data (like Python validation)
        if (!hasCrushingLoad || !hasCompressiveStrength) {
          console.log(`❌ Frontend: Skipping row ${rowIndex} - missing crushing load or compressive strength`);
          return; // Skip incomplete rows
        }
        
        // Create row data object (like Python collectFormData())
        const rowData = {
          cube_id: row.cubeId,
          dimension_length: row.length ? parseFloat(row.length) : null,
          dimension_width: row.breadth ? parseFloat(row.breadth) : null,
          dimension_height: row.height ? parseFloat(row.height) : null,
          weight: row.weight ? parseFloat(row.weight) : null,
          crushing_load: parseFloat(row.crushingLoad),
          compressive_strength: parseFloat(row.compressiveStrength),
          failure_type: row.failureType ? parseFloat(row.failureType) : null
        };
        
        console.log(`✅ Frontend: Created rowData for ${row.cubeId}:`, rowData);
        rowsData.push(rowData);
      });
      
      // Save rowsData to state for use in success modal
      setLastSavedRowsData(rowsData);
      
        // Build observations data like Python system
        const observationsData = {
          rows: rowsData, // Main data array like Python
          sample_description: formData.sampleDescription,
          cube_condition: formData.cubeCondition,
          curing_condition: formData.curingCondition,
          machine_used: formData.machineUsed,
          test_method: formData.testMethod,
          average_strength: formData.averageStrength ? parseFloat(formData.averageStrength) : null,
          tested_by: formData.testedBy,
          checked_by: formData.checkedBy,
          verified_by: formData.verifiedBy,
          test_remarks: formData.testRemarks,
          capturedImages: capturedImages,
          testRequestId: testRequestId,
          timestamp: new Date().toISOString(),
          // Add missing fields that should be saved to database
          grade: testData?.grade || testRequest?.grade || 'M25',
          casting_date: testData?.castingDate || testRequest?.castingDate || null,
          testing_date: testData?.testingDate || testRequest?.testingDate || null,
          age_in_days: testData?.ageInDays || testRequest?.ageInDays || null,
          sample_code_number: testData?.sampleCodeNumber || testRequest?.sampleCodeNumber || testData?.referenceNumber || testRequest?.referenceNumber || null,
          location_nature: testData?.locationNature || testRequest?.locationNature || null,
          id_mark: testData?.idMark || testRequest?.idMark || `C${rowsData.length > 0 ? rowsData[0].cube_id : '1'}`,
          // Add observations data that will be displayed in PDF
          obs_strength_duration: formData.obsStrengthDuration || '--',
          obs_test_results: formData.obsTestResults || '--',
          obs_weight: formData.obsWeight || '--',
          obs_failure_pattern: formData.obsFailurePattern || '--',
          obs_bonding: formData.obsBonding || '--',
          obs_strength_criteria: formData.obsStrengthCriteria || '--'
        };
      console.log('  rowsData content:', rowsData);
      console.log('  observationsData:', observationsData);

      setSaveProgress(30);

      // Validate we have a test request ID before saving
      if (!testRequestId) {
        setErrorMessage('Error: Test Request ID is missing. Cannot save observations.');
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 3000);
        setIsSubmitting(false);
        setSaveProgress(0);
        return;
      }

      try {
        // Pre-upload validation: Check total payload size
        const payloadSize = JSON.stringify(observationsData).length;
        const payloadSizeKB = Math.round(payloadSize / 1024);
        console.log(`📦 Total payload size: ${payloadSizeKB}KB`);
        
        if (payloadSizeKB > 200000) { // 200MB limit for large images
          // Try to compress all images before rejecting
          console.log('⚠️ Large payload detected, attempting to compress all images...');
          const compressedData = { ...observationsData };
          
          if (compressedData.capturedImages) {
            let totalCompressed = 0;
            Object.keys(compressedData.capturedImages).forEach(key => {
              const imageData = compressedData.capturedImages[key];
              if (imageData && imageData.startsWith('data:image')) {
                // Create a temporary canvas to compress the image
                const img = new Image();
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.width = Math.floor(img.width * 0.6);
                  canvas.height = Math.floor(img.height * 0.6);
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  compressedData.capturedImages[key] = canvas.toDataURL('image/jpeg', 0.2);
                  totalCompressed++;
                  
                  // Check if all images are compressed
                  if (totalCompressed === Object.keys(compressedData.capturedImages).length) {
                    const newPayloadSize = JSON.stringify(compressedData).length;
                    const newPayloadSizeKB = Math.round(newPayloadSize / 1024);
                    console.log(`📦 Compressed payload size: ${newPayloadSizeKB}KB`);
                    
                    if (newPayloadSizeKB > 200000) {
                      setErrorMessage('Total data size is too large even after compression. Please reduce the number of images or try again with fewer photos.');
                      setShowErrorModal(true);
                      setTimeout(() => setShowErrorModal(false), 5000);
                      setIsSubmitting(false);
                      setSaveProgress(0);
                      return;
                    } else {
                      // Use compressed data
                      observationsData = compressedData;
                      console.log('✅ Payload compressed successfully, proceeding with upload...');
                    }
                  }
                };
                img.src = imageData;
              }
            });
          } else {
            setErrorMessage('Total data size is too large. Please reduce the number of images or try again with fewer photos.');
            setShowErrorModal(true);
            setTimeout(() => setShowErrorModal(false), 5000);
            setIsSubmitting(false);
            setSaveProgress(0);
            return;
          }
        }
        
        // Try to save to database first (no artificial progress delay)
        const response = await axios.post(`${API_BASE_URL}/test-observations/${testRequestId}`, observationsData, {
          timeout: 120000, // 120 second timeout for large image uploads
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log('✅ Backend response:', response.data);
        
        if (response.data && response.data.message) {
          setSubmitMessage({ type: 'success', text: `Test observations saved successfully! ${response.data.images_saved || 0} images saved.` });
        } else {
          setSubmitMessage({ type: 'success', text: 'Test observations saved successfully!' });
        }
        setShowSuccessModal(true);
      } catch (dbError) {
        console.error('❌ Database save failed:', dbError);
        console.error('Error details:', dbError.response?.data);
        
        // Handle specific error types with retry logic for 413 errors
        let errorMessage = 'Failed to save to database. Please try again.';
        let shouldRetry = false;
        
        if (dbError.response?.status === 413) {
          errorMessage = 'Image file is too large. Attempting automatic compression and retry...';
          shouldRetry = true;
          
          // Try to compress images further and retry once
          console.log('🔄 413 Error detected, attempting automatic compression...');
          const compressedObservationsData = { ...observationsData };
          
          // Compress all captured images further
          if (compressedObservationsData.capturedImages) {
            Object.keys(compressedObservationsData.capturedImages).forEach(key => {
              const imageData = compressedObservationsData.capturedImages[key];
              if (imageData && imageData.startsWith('data:image')) {
                // Create a temporary canvas to compress the image
                const img = new Image();
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.width = Math.floor(img.width * 0.7);
                  canvas.height = Math.floor(img.height * 0.7);
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  compressedObservationsData.capturedImages[key] = canvas.toDataURL('image/jpeg', 0.3);
                };
                img.src = imageData;
              }
            });
          }
          
          // Retry with compressed data
          try {
            console.log('🔄 Retrying with compressed images...');
            const retryResponse = await axios.post(`${API_BASE_URL}/test-observations/${testRequestId}`, compressedObservationsData, {
              timeout: 120000, // 120 second timeout for large uploads
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            console.log('✅ Retry successful:', retryResponse.data);
            if (retryResponse.data && retryResponse.data.message) {
              setSubmitMessage({ type: 'success', text: `Test observations saved successfully after compression! ${retryResponse.data.images_saved || 0} images saved.` });
            } else {
              setSubmitMessage({ type: 'success', text: 'Test observations saved successfully after compression!' });
            }
            setShowSuccessModal(true);
            setSaveProgress(100);
            setIsSubmitting(false);
            return;
          } catch (retryError) {
            console.error('❌ Retry also failed:', retryError);
            errorMessage = 'Image file is too large even after compression. Please try capturing with lower quality or smaller resolution.';
          }
        } else if (dbError.response?.status === 400) {
          errorMessage = 'Invalid data format. Please check your inputs and try again.';
        } else if (dbError.response?.status === 500) {
          errorMessage = 'Server error occurred. Please try again in a few moments.';
        } else if (dbError.response?.data?.error) {
          errorMessage = `Database error: ${dbError.response.data.error}`;
        } else if (dbError.message) {
          errorMessage = `Request failed with status code ${dbError.response?.status || 'unknown'}. Please try again.`;
        }
        
        setErrorMessage(errorMessage);
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 5000);
        
        setIsSubmitting(false);
        setSaveProgress(0);
        return;
      }
      
      setSaveProgress(100);
      
    } catch (error) {
      console.error('Error saving test observations:', error);
      setErrorMessage('Failed to save test observations. Please try again.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setIsSubmitting(false);
      setSaveProgress(0);
    }
  };

  // Camera functionality
  const openCamera = (photoType) => {
    const modal = document.getElementById('cameraModal');
    if (modal) {
      modal.setAttribute('data-photo-type', photoType);
      modal.style.display = 'flex';
      startCamera();
    }
  };

  const [currentFacingMode, setCurrentFacingMode] = useState('environment');
  
  const startCamera = async (facingMode = 'environment') => {
    try {
      const video = document.getElementById('camera');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: facingMode } 
      });
      video.srcObject = stream;
      setCurrentFacingMode(facingMode);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Camera access denied. Please allow camera permissions.');
    }
  };
  
  const switchCamera = () => {
    const newMode = currentFacingMode === 'environment' ? 'user' : 'environment';
    closeCamera();
    setTimeout(() => startCamera(newMode), 100);
  };
  
  const handleDeletePhoto = (photoKey) => {
    setDeletePhotoKey(photoKey);
    setShowDeleteConfirm(true);
  };
  
  const confirmDeletePhoto = () => {
    if (deletePhotoKey) {
      setCapturedImages(prev => {
        const updated = {...prev};
        delete updated[deletePhotoKey];
        return updated;
      });
    }
    setShowDeleteConfirm(false);
    setDeletePhotoKey(null);
  };

  // Resize image to optimize file size while maintaining quality
  const resizeImage = (canvas, maxWidth = 1200, maxHeight = 800) => {
    let width = canvas.width;
    let height = canvas.height;
    
    // Calculate new dimensions while maintaining aspect ratio
    if (width > maxWidth || height > maxHeight) {
      const ratio = Math.min(maxWidth / width, maxHeight / height);
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
      
      // Create a new canvas with resized dimensions
      const resizedCanvas = document.createElement('canvas');
      resizedCanvas.width = width;
      resizedCanvas.height = height;
      const ctx = resizedCanvas.getContext('2d');
      ctx.drawImage(canvas, 0, 0, width, height);
      return resizedCanvas;
    }
    return canvas;
  };

  // Helper function to compress uploaded images with iPhone detection
  const compressUploadedImage = (imageData) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Detect iPhone devices for more aggressive compression
        const isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);
        
        // Use smaller dimensions for iPhone devices
        const maxWidth = isIPhone ? 800 : 1200;
        const maxHeight = isIPhone ? 600 : 800;
        
        console.log(`📱 Upload compression - Device: ${isIPhone ? 'iPhone/iPad' : 'Other'} - Using ${maxWidth}x${maxHeight} max dimensions`);
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        
        const resizedCanvas = resizeImage(canvas, maxWidth, maxHeight);
        
        // Apply compression with optimized quality settings for 20MB images
        let compressedData = resizedCanvas.toDataURL('image/jpeg', 0.85); // Start with very high quality
        let fileSizeKB = Math.round((compressedData.length * 0.75) / 1024);
        
        // Progressive compression levels - optimized for 20MB images with better quality
        const compressionLevels = [
          { quality: 0.85, maxSize: 20000, name: 'Very High Quality' },
          { quality: 0.75, maxSize: 18000, name: 'High Quality' },
          { quality: 0.65, maxSize: 15000, name: 'Standard' },
          { quality: 0.55, maxSize: 12000, name: 'Good Quality' },
          { quality: 0.45, maxSize: 10000, name: 'Medium Compression' },
          { quality: 0.35, maxSize: 8000, name: 'High Compression' },
          { quality: 0.25, maxSize: 6000, name: 'Higher Compression' },
          { quality: 0.15, maxSize: 4000, name: 'Maximum Compression' },
          // iPhone-specific compression levels
          ...(isIPhone ? [
            { quality: 0.1, maxSize: 3000, name: 'iPhone Ultra Compression' },
            { quality: 0.05, maxSize: 2000, name: 'iPhone Maximum Compression' }
          ] : [])
        ];
        
        for (let i = 0; i < compressionLevels.length; i++) {
          const level = compressionLevels[i];
          
          if (fileSizeKB > level.maxSize) {
            console.warn(`⚠️ Upload image too large (${fileSizeKB}KB), applying ${level.name}...`);
            compressedData = resizedCanvas.toDataURL('image/jpeg', level.quality);
            fileSizeKB = Math.round((compressedData.length * 0.75) / 1024);
            console.log(`📸 After ${level.name}: ${fileSizeKB}KB`);
          } else {
            break;
          }
        }
        
        // Final safety check - increased limit for 20MB images
        if (fileSizeKB > 20000) {
          console.error(`❌ Upload image still too large after compression: ${fileSizeKB}KB`);
          setErrorMessage('Uploaded image is too large even after maximum compression. Please try a smaller image or use the camera instead.');
          setShowErrorModal(true);
          setTimeout(() => setShowErrorModal(false), 5000);
          resolve(null);
          return;
        }
        
        console.log(`✅ Upload image compressed to: ${fileSizeKB}KB`);
        resolve(compressedData);
      };
      img.src = imageData;
    });
  };

  const capturePhoto = () => {
    const video = document.getElementById('camera');
    const canvas = document.getElementById('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    // Detect iPhone devices for more aggressive compression
    const isIPhone = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod|iOS/.test(navigator.userAgent);
    
    // Use smaller dimensions for iPhone devices to reduce file size
    const maxWidth = isIPhone ? 800 : 1200;
    const maxHeight = isIPhone ? 600 : 800;
    
    console.log(`📱 Device detected: ${isIPhone ? 'iPhone/iPad' : 'Other'} - Using ${maxWidth}x${maxHeight} max dimensions`);
    
    // Resize image to optimize file size with device-specific dimensions
    const resizedCanvas = resizeImage(canvas, maxWidth, maxHeight);
    
    // Progressive compression with optimized quality settings for 20MB images
    let imageData = resizedCanvas.toDataURL('image/jpeg', 0.85); // Start with very high quality
    let fileSizeKB = Math.round((imageData.length * 0.75) / 1024);
    console.log(`📸 Initial image size: ${fileSizeKB}KB`);
    
    // Progressive compression levels - optimized for larger images with better quality
    const compressionLevels = [
      { quality: 0.75, maxSize: 15000, name: 'High Quality' },
      { quality: 0.65, maxSize: 12000, name: 'Standard' },
      { quality: 0.55, maxSize: 10000, name: 'Good Quality' },
      { quality: 0.45, maxSize: 8000, name: 'Medium Compression' },
      { quality: 0.35, maxSize: 6000, name: 'High Compression' },
      { quality: 0.25, maxSize: 4000, name: 'Higher Compression' },
      { quality: 0.15, maxSize: 3000, name: 'Maximum Compression' },
      // iPhone-specific compression levels
      ...(isIPhone ? [
        { quality: 0.1, maxSize: 2000, name: 'iPhone Ultra Compression' },
        { quality: 0.05, maxSize: 1000, name: 'iPhone Maximum Compression' }
      ] : [])
    ];
    
    let compressionUsed = 'Standard';
    
    for (let i = 0; i < compressionLevels.length; i++) {
      const level = compressionLevels[i];
      
      if (fileSizeKB > level.maxSize) {
        console.warn(`⚠️ Image too large (${fileSizeKB}KB), applying ${level.name}...`);
        imageData = resizedCanvas.toDataURL('image/jpeg', level.quality);
        fileSizeKB = Math.round((imageData.length * 0.75) / 1024);
        compressionUsed = level.name;
        console.log(`📸 After ${level.name}: ${fileSizeKB}KB`);
        
        // If this is the last compression level and still too large, resize the canvas
        if (i === compressionLevels.length - 1 && fileSizeKB > level.maxSize) {
          console.warn('⚠️ Maximum compression reached, resizing canvas...');
          const smallerCanvas = document.createElement('canvas');
          const smallerCtx = smallerCanvas.getContext('2d');
          
          // Reduce canvas size by 20%
          smallerCanvas.width = Math.floor(resizedCanvas.width * 0.8);
          smallerCanvas.height = Math.floor(resizedCanvas.height * 0.8);
          smallerCtx.drawImage(resizedCanvas, 0, 0, smallerCanvas.width, smallerCanvas.height);
          
          imageData = smallerCanvas.toDataURL('image/jpeg', 0.15);
          fileSizeKB = Math.round((imageData.length * 0.75) / 1024);
          console.log(`📸 After canvas resize: ${fileSizeKB}KB`);
        }
      } else {
        break;
      }
    }
    
    // Final safety check - if still too large, reject
    if (fileSizeKB > 5000) { // 5MB final limit
      console.error(`❌ Image still too large after all compression: ${fileSizeKB}KB`);
      setErrorMessage('Image is too large even after maximum compression. Please try capturing with lower resolution or different lighting conditions.');
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 5000);
      setIsUploading(false);
      return;
    }
    
    console.log(`✅ Final image size: ${fileSizeKB}KB (${compressionUsed})`);
    
    // Show loading indicator
    setIsUploading(true);
    
    // Simulate upload process
    setTimeout(() => {
      // Save the image to state
      const currentPhotoType = document.getElementById('cameraModal').getAttribute('data-photo-type');
      if (currentPhotoType) {
        setCapturedImages(prev => ({
          ...prev,
          [currentPhotoType]: imageData
        }));
      }
      
      setIsUploading(false);
      closeCamera();
    }, 2000);
  };

  const closeCamera = () => {
    const modal = document.getElementById('cameraModal');
    const video = document.getElementById('camera');
    
    if (modal) modal.style.display = 'none';
    if (video && video.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
    }
  };

  // Get test request info (from API or state)
  const currentTestRequest = apiTestRequest || testRequest;
  const currentConcreteTests = apiConcreteTests.length > 0 ? apiConcreteTests : (testData ? [testData] : []);
  
  // Debug: Log the data sources
  console.log('🔍 DEBUG - Data Sources:');
  console.log('  apiTestRequest:', apiTestRequest);
  console.log('  testRequest:', testRequest);
  console.log('  currentTestRequest:', currentTestRequest);
  console.log('  loading:', loading);
  
  // Debug: Log the receipt date data
  console.log('🔍 DEBUG - Receipt Date Data:');
  console.log('  apiTestRequest?.receipt_date:', apiTestRequest?.receipt_date);
  console.log('  testRequest?.receiptDate:', testRequest?.receiptDate);
  console.log('  testRequest?.receipt_date:', testRequest?.receipt_date);
  console.log('  currentTestRequest?.receipt_date:', currentTestRequest?.receipt_date);
  console.log('  currentTestRequest?.receiptDate:', currentTestRequest?.receiptDate);
  console.log('  Final receipt date value:', formatDate(apiTestRequest?.receipt_date || currentTestRequest?.receipt_date || testRequest?.receiptDate || testRequest?.receipt_date || ''));
  
  // Debug: Log casting and testing date data
  console.log('🔍 DEBUG - Casting/Testing Date Data:');
  console.log('  apiConcreteTests[0]:', apiConcreteTests[0]);
  console.log('  currentConcreteTests[0]:', currentConcreteTests[0]);
  if (currentConcreteTests[0]) {
    console.log('    - casting_date:', currentConcreteTests[0].casting_date);
    console.log('    - castingDate:', currentConcreteTests[0].castingDate);
    console.log('    - testing_date:', currentConcreteTests[0].testing_date);
    console.log('    - testingDate:', currentConcreteTests[0].testingDate);
  }
  console.log('  testData:', testData);
  if (testData) {
    console.log('    - castingDate:', testData.castingDate);
    console.log('    - testingDate:', testData.testingDate);
  }
  
  return (
    <Container className="py-4">
      
      <style>{`
        .test-results-professional {
          table-layout: fixed;
          width: 100%;
          border-radius: 12px !important;
          overflow: hidden;
          margin: 0 !important;
        }
        
        .test-results-professional th,
        .test-results-professional td {
          text-align: center !important;
          vertical-align: middle !important;
          padding: 8px 6px !important;
          font-size: 0.875rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .test-results-professional input.form-control {
          width: 100% !important;
          text-align: center !important;
          padding: 6px 8px !important;
          font-size: 0.875rem !important;
          border: 2px solid #6c757d !important;
          border-radius: 6px !important;
          background-color: transparent !important;
          color: #fff !important;
          box-sizing: border-box !important;
          min-width: 0 !important;
          height: auto !important;
          min-height: 38px !important;
        }
        
        .test-results-professional input.form-control::placeholder {
          color: #adb5bd !important;
          opacity: 0.8 !important;
        }
        
        .test-results-professional input.form-control:focus {
          border-color: #FFA500 !important;
          box-shadow: 0 0 0 0.25rem rgba(255, 165, 0, 0.25) !important;
          background-color: rgba(255, 165, 0, 0.1) !important;
        }
        
        .test-results-professional input[readonly] {
          background-color: rgba(108, 117, 125, 0.1) !important;
          border-color: #495057 !important;
          opacity: 0.8;
        }
        
        .test-results-professional .invalid-feedback {
          font-size: 0.75rem;
          color: #dc3545 !important;
          margin-top: 2px;
        }
        
        .test-results-professional .is-invalid {
          border-color: #dc3545 !important;
          box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25) !important;
        }
        
        .table th {
          background-color: #6c757d !important;
          color: white !important;
        }
        .table td {
          background-color: #1C2333 !important;
          color: white !important;
        }
        .bg-secondary {
          background-color: #6c757d !important;
        }
        .bg-dark {
          background-color: #1C2333 !important;
        }
      `}</style>

      {/* Breadcrumb */}
      <Row className="mb-4">
        <Col>
          <h1 className="display-5 fw-bold">COMPRESSIVE STRENGTH OF CONCRETE CUBE</h1>
          <p className="lead">Job Number: {testRequest?.jobNumber || 'N/A'}</p>
          <p>Concrete Test: {testData?.idMark || `Test ${testIndex + 1}`}</p>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Button variant="link" className="p-0" onClick={() => navigate('/')}>Home</Button>
              </li>
              <li className="breadcrumb-item">
                <Button variant="link" className="p-0" onClick={() => navigate('/dashboard')}>Dashboard</Button>
              </li>
              <li className="breadcrumb-item">
                <Button variant="link" className="p-0" onClick={() => navigate('/view-sample', { state: { formData: testRequest } })}>Test Details</Button>
              </li>
              <li className="breadcrumb-item active" aria-current="page">Test Observations</li>
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Loading Overlay for Images */}
      {imagesLoading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10001
          }}
        >
          <div 
            style={{
              backgroundColor: '#1C2333',
              padding: '3rem',
              borderRadius: '15px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '2px solid #FFA500',
              minWidth: '400px'
            }}
          >
            <div 
              className="spinner-border text-warning" 
              role="status" 
              style={{ width: '4rem', height: '4rem', borderWidth: '0.3rem' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 className="mt-4 mb-2 text-warning">Loading Images...</h4>
            <div className="progress mt-3" style={{ height: '30px', backgroundColor: '#2a3142' }}>
              <div 
                className="progress-bar progress-bar-striped progress-bar-animated bg-warning" 
                role="progressbar" 
                style={{ width: `${imagesLoadProgress}%`, fontSize: '16px', fontWeight: 'bold' }}
              >
                {imagesLoadProgress}%
              </div>
            </div>
            <p className="text-white mt-3 mb-0">Please wait while images are loading...</p>
          </div>
        </div>
      )}

      {/* Loading Overlay for Data Loading */}
      {loading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
        >
          <div 
            style={{
              backgroundColor: '#1C2333',
              padding: '3rem',
              borderRadius: '15px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              border: '2px solid #FFA500'
            }}
          >
            <div 
              className="spinner-border text-warning" 
              role="status" 
              style={{ width: '4rem', height: '4rem', borderWidth: '0.3rem' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 className="mt-4 mb-2 text-warning">Loading Observation Data...</h4>
            <p className="text-white mb-0">Please wait while we fetch all cube details and images</p>
          </div>
        </div>
      )}

      {/* Loading Overlay for Image Upload */}
      {isUploading && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '10px',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div 
              className="spinner-border text-warning" 
              role="status" 
              style={{ width: '3rem', height: '3rem' }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3">
              <h5 className="text-dark mb-2">Uploading Image...</h5>
              <p className="text-muted mb-0">Please wait while we process your photo</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay for Data Saving */}
      {isSubmitting && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '3rem',
              borderRadius: '15px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
              minWidth: '400px',
              maxWidth: '500px'
            }}
          >
            <div className="mb-4">
              <div 
                className="spinner-border text-warning" 
                role="status" 
                style={{ width: '4rem', height: '4rem' }}
              >
                <span className="visually-hidden">Saving...</span>
              </div>
            </div>
            
            <h4 className="text-dark mb-3">Saving Test Observations</h4>
            <p className="text-muted mb-4">Please wait while we save your data...</p>
            
            {/* Progress Bar */}
            <div className="progress mb-3" style={{ height: '8px', borderRadius: '4px' }}>
              <div 
                className="progress-bar bg-warning progress-bar-striped progress-bar-animated" 
                role="progressbar" 
                style={{ width: `${saveProgress}%` }}
                aria-valuenow={saveProgress} 
                aria-valuemin="0" 
                aria-valuemax="100"
              >
              </div>
            </div>
            
            <div className="d-flex justify-content-between text-muted small">
              <span>Preparing data...</span>
              <span>{Math.round(saveProgress)}%</span>
              <span>Processing...</span>
            </div>
            
            {/* Status Messages */}
            <div className="mt-3">
              {saveProgress < 30 && (
                <small className="text-info">Validating form data...</small>
              )}
              {saveProgress >= 30 && saveProgress < 60 && (
                <small className="text-info">Preparing for save...</small>
              )}
              {saveProgress >= 60 && saveProgress < 90 && (
                <small className="text-info">Saving to database...</small>
              )}
              {saveProgress >= 90 && (
                <small className="text-success">Almost done...</small>
              )}
            </div>
          </div>
        </div>
      )}

      <Row>
        <Col>
          <Card className="bg-dark">
            <Card.Header className="text-white" style={{ backgroundColor: '#FFA500' }}>
              <h3 className="card-title mb-0">Enter Test Observations</h3>
            </Card.Header>
            <Card.Body>
              {/* Success/Error Message */}
              {submitMessage.text && (
                <Alert variant={submitMessage.type} className="mb-4">
                  {submitMessage.text}
                </Alert>
              )}
              
              <Form onSubmit={handleSubmit}>
                {/* Concrete Cube Details */}
                <Row className="mb-4">
                  <Col md={12}>
                    <h4 className="mb-3 text-white">Concrete Cube Details</h4>
                  </Col>
                  
                  <div className="table-responsive">
                    <Table bordered className="table-dark">
                      <tbody>
                        <tr>
                          <td className="bg-secondary" width="25%">Reference Number</td>
                          <td width="25%">{testRequest?.referenceNumber || currentConcreteTests?.[0]?.sample_code_number || 'N/A'}</td>
                          <td className="bg-secondary" width="25%">Sample Description</td>
                          <td width="25%">
                            <Form.Control
                              name="sampleDescription"
                              value={formData.sampleDescription}
                              onChange={handleInputChange}
                              readOnly
                              className="bg-transparent text-white"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-secondary">Quantity of Cubes</td>
                          <td>{currentConcreteTests?.[0]?.quantity || testRows.length}</td>
                          <td className="bg-secondary">Date of Receipt</td>
                          <td>
                            {loading ? (
                              <span className="text-muted">Loading...</span>
                            ) : (
                              formatDate(apiTestRequest?.receipt_date || currentTestRequest?.receipt_date || testRequest?.receiptDate || testRequest?.receipt_date || '')
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-secondary">Date of Casting</td>
                          <td>
                            {(() => {
                              // Try multiple sources for casting date
                              const castingDate = currentConcreteTests?.[0]?.casting_date || 
                                                currentConcreteTests?.[0]?.castingDate || 
                                                testData?.castingDate || 
                                                urlParams.get('date_of_casting') || 
                                                '';
                              
                              console.log('🔍 DEBUG - Date of Casting field:', {
                                currentConcreteTests: currentConcreteTests?.[0],
                                testData,
                                urlParams: Array.from(urlParams.entries()),
                                castingDate,
                                formatted: formatDate(castingDate)
                              });
                              return formatDate(castingDate);
                            })()}
                          </td>
                          <td className="bg-secondary">Date of Testing</td>
                          <td>
                            {(() => {
                              // Try multiple sources for testing date
                              const testingDate = currentConcreteTests?.[0]?.testing_date || 
                                                currentConcreteTests?.[0]?.testingDate || 
                                                testData?.testingDate || 
                                                urlParams.get('date_of_testing') || 
                                                '';
                              
                              // If no testing date is provided, use current date as fallback
                              const currentDate = new Date();
                              const currentDateFormatted = `${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getFullYear()}`;
                              const finalTestingDate = testingDate || currentDateFormatted;
                              
                              console.log('🔍 DEBUG - Date of Testing field:', {
                                currentConcreteTests: currentConcreteTests?.[0],
                                testData,
                                urlParams: Array.from(urlParams.entries()),
                                testingDate,
                                finalTestingDate,
                                formatted: formatDate(finalTestingDate)
                              });
                              return formatDate(finalTestingDate);
                            })()}
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-secondary">Grade of Cube</td>
                          <td>{currentConcreteTests?.[0]?.grade || testData?.grade || ""}</td>
                          <td className="bg-secondary">Cube Condition</td>
                          <td>
                            <Form.Control
                              name="cubeCondition"
                              value={formData.cubeCondition}
                              onChange={handleInputChange}
                              readOnly
                              className="bg-transparent text-white"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-secondary">Age of Cube</td>
                          <td>
                            {(() => {
                              // Try multiple possible field names for casting and testing dates
                              const casting = currentConcreteTests?.[0]?.castingDate || 
                                            currentConcreteTests?.[0]?.casting_date || 
                                            testData?.castingDate || 
                                            testData?.casting_date ||
                                            urlParams.get('date_of_casting') || 
                                            '';
                              const currentDate = new Date();
                              const currentDateFormatted = `${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getFullYear()}`;
                              const testing = currentConcreteTests?.[0]?.testingDate || 
                                            currentConcreteTests?.[0]?.testing_date || 
                                            testData?.testingDate || 
                                            testData?.testing_date ||
                                            urlParams.get('date_of_testing') || 
                                            currentDateFormatted; // Fallback to current date
                              
                              console.log('🔍 DEBUG - Age of Cube Calculation:', {
                                currentConcreteTests: currentConcreteTests?.[0],
                                testData,
                                casting,
                                testing,
                                castingType: typeof casting,
                                testingType: typeof testing
                              });
                              
                              if (casting && testing) {
                                try {
                                  // Handle DD/MM/YYYY format
                                  const parseDate = (dateStr) => {
                                    console.log('🔍 DEBUG - Parsing date:', dateStr);
                                    
                                    // Handle truncated year (e.g., "15/10/202" should be "15/10/2025")
                                    if (typeof dateStr === 'string' && /^\d{1,2}\/\d{1,2}\/\d{3}$/.test(dateStr)) {
                                      const [day, month, year] = dateStr.split('/');
                                      const fullYear = year.length === 3 ? '2' + year : year; // Assume 20xx for 3-digit years
                                      const correctedDateStr = `${day}/${month}/${fullYear}`;
                                      console.log('🔍 DEBUG - Corrected truncated year:', { original: dateStr, corrected: correctedDateStr });
                                      const parsedDate = new Date(fullYear, month - 1, day);
                                      console.log('🔍 DEBUG - Parsed corrected date:', parsedDate);
                                      return parsedDate;
                                    }
                                    
                                    if (typeof dateStr === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
                                      const [day, month, year] = dateStr.split('/');
                                      const parsedDate = new Date(year, month - 1, day);
                                      console.log('🔍 DEBUG - Parsed DD/MM/YYYY:', { day, month, year, parsedDate });
                                      return parsedDate;
                                    }
                                    
                                    // Handle YYYY-MM-DD format
                                    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                                      const parsedDate = new Date(dateStr);
                                      console.log('🔍 DEBUG - Parsed YYYY-MM-DD:', parsedDate);
                                      return parsedDate;
                                    }
                                    
                                    const parsedDate = new Date(dateStr);
                                    console.log('🔍 DEBUG - Parsed other format:', parsedDate);
                                    return parsedDate;
                                  };
                                  
                                  const castingDate = parseDate(casting);
                                  const testingDate = parseDate(testing);
                                  
                                  console.log('🔍 DEBUG - Final dates:', { castingDate, testingDate });
                                  
                                  if (isNaN(castingDate.getTime()) || isNaN(testingDate.getTime())) {
                                    console.warn('🔍 DEBUG - Invalid dates:', { castingDate, testingDate });
                                    return 'N/A days';
                                  }
                                  
                                  const diffTime = testingDate - castingDate;
                                  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                  const result = Math.max(0, diffDays);
                                  
                                  console.log('🔍 DEBUG - Age calculation result:', { diffTime, diffDays, result });
                                  return result + ' days';
                                } catch (error) {
                                  console.error('🔍 DEBUG - Error calculating age:', error);
                                  return 'N/A days';
                                }
                              }
                              
                              console.log('🔍 DEBUG - Missing dates, returning N/A');
                              return 'N/A days';
                            })()}
                          </td>
                          <td className="bg-secondary">Curing Condition <span className="text-danger">*</span></td>
                          <td>
                            <Form.Control
                              name="curingCondition"
                              value={formData.curingCondition}
                              onChange={handleInputChange}
                              placeholder="Enter curing condition (Required)"
                              required
                              className="bg-transparent text-white"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-secondary" width="25%">Machine used for Testing</td>
                          <td colSpan="3">
                            <Form.Control
                              name="machineUsed"
                              value={formData.machineUsed}
                              onChange={handleInputChange}
                              readOnly
                              className="bg-transparent text-white"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="bg-secondary" width="25%">Test Method</td>
                          <td colSpan="3">
                            <Form.Control
                              name="testMethod"
                              value={formData.testMethod}
                              onChange={handleInputChange}
                              readOnly
                              className="bg-transparent text-white"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Row>
                
                {/* Test Results */}
                <Row className="mb-4">
                  <Col md={12}>
                    <h4 className="mb-3 text-white">Test Results</h4>
                  </Col>
                  
                  <div className="table-responsive p-0 rounded" style={{ borderRadius: '12px', overflowX: 'auto', overflowY: 'visible', margin: 0, border: '2px solid #495057', maxHeight: 'none' }}>
                    <Table bordered className="table-dark test-results-professional mb-0" style={{ minWidth: '1200px', whiteSpace: 'nowrap' }}>
                      <thead className="bg-secondary">
                        <tr>
                          <th className="text-center">Sr. No.</th>
                          <th className="text-center">Cube ID</th>
                          <th className="text-center">Length (mm) <span className="text-danger">*</span></th>
                          <th className="text-center">Breadth (mm) <span className="text-danger">*</span></th>
                          <th className="text-center">Height (mm) <span className="text-danger">*</span></th>
                          <th className="text-center">Area (mm²)</th>
                          <th className="text-center">Cube Weight <span className="text-danger">*</span></th>
                          <th className="text-center">Density</th>
                          <th className="text-center">Maximum Load <span className="text-danger">*</span></th>
                          <th className="text-center">Compressive Strength <span className="text-danger">*</span></th>
                          <th className="text-center">Type of Failure <span className="text-muted">(Optional)</span></th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testRows.map((row, index) => (
                          <tr key={row.id}>
                            <td className="text-center">{index + 1}</td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.cubeId}
                                onChange={(e) => handleRowChange(index, 'cubeId', e.target.value)}
                                placeholder="Cube ID"
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.length}
                                onChange={(e) => handleRowChange(index, 'length', e.target.value)}
                                placeholder="Length"
                                step="0.0001"
                                min="0"
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.breadth}
                                onChange={(e) => handleRowChange(index, 'breadth', e.target.value)}
                                placeholder="Breadth"
                                step="0.0001"
                                min="0"
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.height}
                                onChange={(e) => handleRowChange(index, 'height', e.target.value)}
                                placeholder="Height"
                                step="0.0001"
                                min="0"
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.area}
                                readOnly
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.weight}
                                onChange={(e) => handleRowChange(index, 'weight', e.target.value)}
                                placeholder="Weight"
                                step="0.0001"
                                className={`text-center ${validationErrors[`weight_${index}`] ? 'is-invalid' : ''}`}
                              />
                              {validationErrors[`weight_${index}`] && (
                                <div className="invalid-feedback d-block text-center">
                                  {validationErrors[`weight_${index}`]}
                                </div>
                              )}
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                value={row.density}
                                readOnly
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.crushingLoad}
                                onChange={(e) => handleRowChange(index, 'crushingLoad', e.target.value)}
                                placeholder="Load"
                                step="0.0001"
                                min="0"
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.compressiveStrength}
                                onChange={(e) => handleRowChange(index, 'compressiveStrength', e.target.value)}
                                placeholder="Strength"
                                step="0.0001"
                                min="0"
                                required
                                className="text-center"
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                value={row.failureType}
                                onChange={(e) => handleRowChange(index, 'failureType', e.target.value)}
                                placeholder="Failure Type"
                                step="0.0001"
                                className="text-center"
                              />
                            </td>
                            <td className="text-center">
                              {testRows.length > 1 && (
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => removeRow(index)}
                                  title="Delete Row"
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-secondary">
                          <td colSpan="9" className="text-end fw-bold pe-3">
                            <div className="d-flex flex-column align-items-end">
                              <span>Average:</span>
                              <small className="text-warning">(Can edit average if needed)</small>
                            </div>
                          </td>
                          <td className="text-center">
                            <Form.Control
                              type="number"
                              name="averageStrength"
                              value={formData.averageStrength}
                              onChange={handleInputChange}
                              className="text-center"
                              placeholder="Enter Average"
                              step="0.0001"
                              min="0"
                              required
                              style={{ width: '120px', minWidth: '120px' }}
                            />
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                  
                  
                  {/* Remarks Section */}
                  <Row className="my-4">
                    <Col md={12}>
                      <div className="p-3 rounded" style={{ border: '2px solid #495057', backgroundColor: 'rgba(73, 80, 87, 0.1)' }}>
                        <Form.Label className="fw-bold mb-3 text-white">Remarks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="testRemarks"
                          value={formData.testRemarks}
                          onChange={handleInputChange}
                          placeholder="Additional observations or notes about the test results"
                          className="bg-transparent text-white"
                        />
                      </div>
                    </Col>
                  </Row>
                  
                  {/* Test verification section */}
                  <div className="table-responsive mt-4">
                    <Table bordered className="table-dark">
                      <tbody>
                        <tr>
                          <td width="33%">Tested By: <span className="text-danger">*</span></td>
                          <td width="33%">Checked By: <span className="text-danger">*</span></td>
                          <td width="33%">Verified By:</td>
                        </tr>
                        <tr>
                          <td>
                            <Form.Select
                              name="testedBy"
                              value={formData.testedBy}
                              onChange={handleInputChange}
                              className="bg-transparent text-white"
                              required
                            >
                              <option value="">Select name</option>
                              {reviewers.map((reviewer) => (
                                <option key={reviewer.id} value={reviewer.name}>
                                  {reviewer.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Select
                              name="checkedBy"
                              value={formData.checkedBy}
                              onChange={handleInputChange}
                              className="bg-transparent text-white"
                              required
                            >
                              <option value="">Select name</option>
                              {reviewers.map((reviewer) => (
                                <option key={reviewer.id} value={reviewer.name}>
                                  {reviewer.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              name="verifiedBy"
                              value={formData.verifiedBy}
                              readOnly
                              className="bg-transparent text-white"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-white">Date:</span>
                              <Form.Control
                                type="date"
                                className="form-control-sm bg-transparent text-white"
                                defaultValue={new Date().toISOString().split('T')[0]}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-white">Date:</span>
                              <Form.Control
                                type="date"
                                className="form-control-sm bg-transparent text-white"
                                defaultValue={new Date().toISOString().split('T')[0]}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-white">Date:</span>
                              <Form.Control
                                type="date"
                                className="form-control-sm bg-transparent text-white"
                                defaultValue={new Date().toISOString().split('T')[0]}
                              />
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Sign:</td>
                          <td>Sign:</td>
                          <td>Sign:</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </Row>

                {/* Photo Capture Section */}
                <div className="mt-5 pt-4">
                  <div className="d-flex align-items-center mb-4 p-3 bg-dark rounded-3 border">
                    <div className="bg-primary rounded-circle p-3 me-3">
                      <i className="fas fa-camera fa-lg text-white"></i>
                    </div>
                    <div>
                      <h4 className="mb-1 text-white">Cube Test Photos</h4>
                      <p className="text-muted mb-0">Capture photos of cube specimens during testing</p>
                    </div>
                  </div>
                  
                  {testRows.map((row, cubeNum) => (
                    <Card key={row.id} className="mb-4 bg-dark border-primary shadow-sm">
                      <Card.Header className="bg-primary text-white border-0">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-cube me-2"></i>
                          <h6 className="mb-0 fw-bold">Cube/Core Specimen #{cubeNum + 1}</h6>
                          <small className="text-warning ms-2">
                            ({Object.keys(capturedImages).filter(key => key.includes(`_${cubeNum + 1}`)).length} images)
                          </small>
                          <small className="text-info ms-2">
                            Keys: {Object.keys(capturedImages).filter(key => key.includes(`_${cubeNum + 1}`)).join(', ')}
                          </small>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-4">
                        <Row className="g-3">
                          {/* Front Failure Photo */}
                          <Col md={4}>
                            <div className="text-center mb-3">
                              <h6 className="text-white fw-semibold mb-0">Front Side Failure</h6>
                            </div>
                            <div className="photo-container border border-secondary rounded-3 bg-dark" style={{ height: '200px', position: 'relative', overflow: 'hidden' }}>
                              {capturedImages[`front_failure_${cubeNum + 1}`] ? (
                                <>
                                  <img 
                                    src={capturedImages[`front_failure_${cubeNum + 1}`]} 
                                    alt="Front Failure" 
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'contain', 
                                      borderRadius: '8px'
                                    }}
                                    onLoad={() => console.log(`✅ Image loaded: front_failure_${cubeNum + 1}`)}
                                    onError={(e) => {
                                      console.log(`❌ Image failed to load: front_failure_${cubeNum + 1}`);
                                      console.log(`❌ Image src:`, e.target.src);
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeletePhoto(`front_failure_${cubeNum + 1}`)}
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                      padding: '5px 10px',
                                      zIndex: 10
                                    }}
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </>
                              ) : (
                                <div className="no-photo-placeholder text-center d-flex flex-column align-items-center justify-content-center" style={{ height: '100%' }}>
                                  <i className="fas fa-image fa-2x text-muted"></i>
                                  <p className="text-muted">No photo</p>
                                </div>
                              )}
                            </div>
                            <div className="photo-controls mt-3">
                              <div className="d-grid gap-2">
                                <Button 
                                  variant="warning" 
                                  className="fw-semibold"
                                  onClick={() => openCamera(`front_failure_${cubeNum + 1}`)}
                                >
                                  <i className="fas fa-camera me-2"></i>Capture
                                </Button>
                                <Button 
                                  variant="outline-light"
                                  onClick={() => document.getElementById(`upload-front-${cubeNum + 1}`).click()}
                                >
                                  <i className="fas fa-upload me-2"></i>Upload
                                </Button>
                                <input
                                  type="file"
                                  id={`upload-front-${cubeNum + 1}`}
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = async (event) => {
                                        // Compress the uploaded image with iPhone detection
                                        const compressedData = await compressUploadedImage(event.target.result);
                                        if (compressedData) {
                                          setCapturedImages(prev => ({
                                            ...prev,
                                            [`front_failure_${cubeNum + 1}`]: compressedData
                                          }));
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </Col>
                          
                          {/* Digital Reading Photo */}
                          <Col md={4}>
                            <div className="text-center mb-3">
                              <h6 className="text-white fw-semibold mb-0">Digital Reading</h6>
                            </div>
                            <div className="photo-container border border-secondary rounded-3 bg-dark" style={{ height: '200px', position: 'relative', overflow: 'hidden' }}>
                              {capturedImages[`digital_reading_${cubeNum + 1}`] ? (
                                <>
                                  <img 
                                    src={capturedImages[`digital_reading_${cubeNum + 1}`]} 
                                    alt="Digital Reading" 
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'contain', 
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeletePhoto(`digital_reading_${cubeNum + 1}`)}
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                      padding: '5px 10px',
                                      zIndex: 10
                                    }}
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </>
                              ) : (
                                <div className="no-photo-placeholder text-center d-flex flex-column align-items-center justify-content-center" style={{ height: '100%' }}>
                                  <i className="fas fa-image fa-2x text-muted"></i>
                                  <p className="text-muted">No photo</p>
                                </div>
                              )}
                            </div>
                            <div className="photo-controls mt-3">
                              <div className="d-grid gap-2">
                                <Button 
                                  variant="warning" 
                                  className="fw-semibold"
                                  onClick={() => openCamera(`digital_reading_${cubeNum + 1}`)}
                                >
                                  <i className="fas fa-camera me-2"></i>Capture
                                </Button>
                                <Button 
                                  variant="outline-light"
                                  onClick={() => document.getElementById(`upload-digital-${cubeNum + 1}`).click()}
                                >
                                  <i className="fas fa-upload me-2"></i>Upload
                                </Button>
                                <input
                                  type="file"
                                  id={`upload-digital-${cubeNum + 1}`}
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = async (event) => {
                                        // Compress the uploaded image with iPhone detection
                                        const compressedData = await compressUploadedImage(event.target.result);
                                        if (compressedData) {
                                          setCapturedImages(prev => ({
                                            ...prev,
                                            [`digital_reading_${cubeNum + 1}`]: compressedData
                                          }));
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </Col>
                          
                          {/* Back Failure Photo */}
                          <Col md={4}>
                            <div className="text-center mb-3">
                              <h6 className="text-white fw-semibold mb-0">Back Side Failure</h6>
                            </div>
                            <div className="photo-container border border-secondary rounded-3 bg-dark" style={{ height: '200px', position: 'relative', overflow: 'hidden' }}>
                              {capturedImages[`back_failure_${cubeNum + 1}`] ? (
                                <>
                                  <img 
                                    src={capturedImages[`back_failure_${cubeNum + 1}`]} 
                                    alt="Back Failure" 
                                    style={{ 
                                      width: '100%', 
                                      height: '100%', 
                                      objectFit: 'contain', 
                                      borderRadius: '8px'
                                    }}
                                  />
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeletePhoto(`back_failure_${cubeNum + 1}`)}
                                    style={{
                                      position: 'absolute',
                                      top: '5px',
                                      right: '5px',
                                      padding: '5px 10px',
                                      zIndex: 10
                                    }}
                                  >
                                    <i className="fas fa-times"></i>
                                  </Button>
                                </>
                              ) : (
                                <div className="no-photo-placeholder text-center d-flex flex-column align-items-center justify-content-center" style={{ height: '100%' }}>
                                  <i className="fas fa-image fa-2x text-muted"></i>
                                  <p className="text-muted">No photo</p>
                                </div>
                              )}
                            </div>
                            <div className="photo-controls mt-3">
                              <div className="d-grid gap-2">
                                <Button 
                                  variant="warning" 
                                  className="fw-semibold"
                                  onClick={() => openCamera(`back_failure_${cubeNum + 1}`)}
                                >
                                  <i className="fas fa-camera me-2"></i>Capture
                                </Button>
                                <Button 
                                  variant="outline-light"
                                  onClick={() => document.getElementById(`upload-back-${cubeNum + 1}`).click()}
                                >
                                  <i className="fas fa-upload me-2"></i>Upload
                                </Button>
                                <input
                                  type="file"
                                  id={`upload-back-${cubeNum + 1}`}
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      const reader = new FileReader();
                                      reader.onload = async (event) => {
                                        // Compress the uploaded image with iPhone detection
                                        const compressedData = await compressUploadedImage(event.target.result);
                                        if (compressedData) {
                                          setCapturedImages(prev => ({
                                            ...prev,
                                            [`back_failure_${cubeNum + 1}`]: compressedData
                                          }));
                                        }
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="mt-4 border-top pt-3">
                  <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                    <Button 
                      variant="outline-light" 
                      className="me-md-2"
                      onClick={() => navigate('/view-sample', { state: { formData: testRequest } })}
                    >
                      <i className="fas fa-times me-2"></i> Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      variant="warning" 
                      className="me-md-2"
                      disabled={isSubmitting}
                      style={{ minWidth: '200px' }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving... ({Math.round(saveProgress)}%)
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i> Save Test Observations
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Success Modal */}
      <Modal 
        show={showSuccessModal} 
        backdrop="static" 
        keyboard={false} 
        centered
      >
        <Modal.Header style={{ backgroundColor: '#198754', borderBottom: 'none' }}>
          <Modal.Title style={{ color: '#fff' }}>
            <i className="fas fa-check-circle me-2"></i>Success!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1a1a1a', color: '#fff', textAlign: 'center', padding: '40px' }}>
          <i className="fas fa-check-circle fa-4x mb-3" style={{ color: '#198754' }}></i>
          <h4>Test Observations Saved Successfully!</h4>
          <p className="text-muted mt-3">Your data has been saved to the database.</p>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: '1px solid #444' }}>
          <Button 
            variant="success" 
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/strength-graph', { 
                replace: true, // Replace current history entry
                state: { 
                  formData: testRequest,
                  testData: testData,
                  testIndex: testIndex,
                  testRequestId: testRequestId,
                  observationsData: {
                    rows: lastSavedRowsData, // Main data array like Python
                    sample_description: formData.sampleDescription,
                    cube_condition: formData.cubeCondition,
                    curing_condition: formData.curingCondition,
                    machine_used: formData.machineUsed,
                    test_method: formData.testMethod,
                    average_strength: formData.averageStrength ? parseFloat(formData.averageStrength) : null,
                    tested_by: formData.testedBy,
                    checked_by: formData.checkedBy,
                    verified_by: formData.verifiedBy,
                    test_remarks: formData.testRemarks,
                    capturedImages: capturedImages,
                    testRequestId: testRequestId,
                    timestamp: new Date().toISOString()
                  }
                } 
              });
            }}
          >
            Continue to Graph <i className="fas fa-arrow-right ms-2"></i>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Modal - Auto-dismisses after 3 seconds */}
      <Modal 
        show={showErrorModal} 
        onHide={() => setShowErrorModal(false)}
        backdrop="static" 
        keyboard={false} 
        centered
      >
        <Modal.Header style={{ backgroundColor: '#dc3545', borderBottom: 'none' }}>
          <Modal.Title style={{ color: '#fff' }}>
            <i className="fas fa-exclamation-circle me-2"></i>Validation Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1a1a1a', color: '#fff', textAlign: 'center', padding: '40px' }}>
          <i className="fas fa-exclamation-circle fa-4x mb-3" style={{ color: '#dc3545' }}></i>
          <h4>{errorMessage}</h4>
          <p className="text-muted mt-3">This message will close automatically in 3 seconds.</p>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: '1px solid #444' }}>
          <Button 
            variant="outline-light" 
            onClick={() => setShowErrorModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#2c2c2c', borderBottom: '1px solid #444' }}>
          <Modal.Title style={{ color: '#fff' }}>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1a1a1a', color: '#fff', textAlign: 'center', padding: '30px' }}>
          <i className="fas fa-exclamation-triangle fa-3x mb-3" style={{ color: '#ffc107' }}></i>
          <h5>Are you sure you want to delete this photo?</h5>
          <p className="text-muted mt-2">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: '1px solid #444' }}>
          <Button variant="outline-light" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeletePhoto}>
            <i className="fas fa-trash me-2"></i>Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Camera Modal */}
      <div 
        id="cameraModal" 
        style={{ 
          display: 'none', 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          background: 'rgba(0,0,0,0.8)', 
          zIndex: 9999, 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}
      >
        <div style={{ 
          background: 'white', 
          padding: '20px', 
          borderRadius: '10px', 
          textAlign: 'center',
          maxWidth: '500px',
          width: '90%'
        }}>
          <h5 className="mb-3">Capture Photo</h5>
          <video 
            id="camera" 
            width="400" 
            height="300" 
            style={{ border: '1px solid #ccc', borderRadius: '8px' }}
            autoPlay
          ></video>
          <canvas id="canvas" style={{ display: 'none' }}></canvas>
          <div className="mt-3 d-flex gap-2 justify-content-center">
            <Button variant="info" onClick={switchCamera}>
              <i className="fas fa-sync-alt me-2"></i>Switch Camera
            </Button>
            <Button variant="primary" onClick={capturePhoto}>
              <i className="fas fa-camera me-2"></i>Capture
            </Button>
            <Button variant="secondary" onClick={closeCamera}>
              <i className="fas fa-times me-2"></i>Cancel
            </Button>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default TestObservations;
